/**
 * PlatformDashboardPage.tsx — Platform admin overview dashboard
 *
 * Shows cross-tenant metrics: total tenants, active tenants,
 * total revenue processed, platform fees collected.
 *
 * @since 2026-06-19 (SaaS conversion — Phase 7)
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import PlatformLayout from '../components/PlatformLayout';
import StatCard from '../components/StatCard';

export default function PlatformDashboardPage(): React.JSX.Element {
  const { token, apiBaseUrl } = useAdminAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['platform', 'revenue'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/platform/revenue`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return res.json() as Promise<{ stats: Record<string, unknown> }>;
    },
    enabled: !!token,
  });

  return (
    <PlatformLayout title="Platform Dashboard">
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard
            label="Total Tenants"
            value={isLoading ? '...' : String(data?.stats?.total_tenants ?? 0)}
          />
          <StatCard
            label="Active Tenants"
            value={isLoading ? '...' : String(data?.stats?.active_tenants ?? 0)}
          />
          <StatCard
            label="Status"
            value="Operational"
          />
        </div>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {String(error)}
          </div>
        )}
      </div>
    </PlatformLayout>
  );
}
